/* eslint-disable jira/filename-case */
import { defineMessage } from '@atlassian/jira-intl';
import type { JiraFavouriteType } from '../ui/starred-menu/__generated__/StarredMenuQuery.graphql.ts';
import type { favoriteTypeFilterMap, favoriteTypeMap } from './types.tsx';

const projects = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.projects',

	defaultMessage: 'Projects',
	description: 'Message for projects nav menu item in the sidebar',
});

const filters = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.filters',

	defaultMessage: 'Filters',
	description: 'Message for filters nav menu item in the sidebar',
});

const dashboards = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.dashboards',

	defaultMessage: 'Dashboards',
	description: 'Message for dashboards nav menu item in the sidebar',
});

const plans = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.plans',

	defaultMessage: 'Plans',
	description: 'Message for plans nav menu item in the sidebar',
});

const boards = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.boards',

	defaultMessage: 'Boards',
	description: 'Message for boards nav menu item in the sidebar',
});

const queues = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.queues',

	defaultMessage: 'Queues',
	description: 'Message for queues nav menu item in the sidebar',
});

const all = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.all',

	defaultMessage: 'All',
	description: 'Message for all nav menu item in the sidebar',
});

const projectsGalaxia = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.projects-galaxia',

	defaultMessage: 'Spaces',
	description: 'Message for projects nav menu item in the sidebar',
});

const roadmaps = defineMessage({
	id: 'navigation-apps-sidebar-nav4-starred.common.roadmaps',
	defaultMessage: 'Roadmaps',
	description: 'Message for roadmaps nav menu item in the sidebar',
});

export const PACKAGE_NAME = 'jiraNavigationAppsSidebarNav4Starred';
export const TEAM_NAME = 'jira-cosmos';
export const FAVOURITE_QUERY_LIMIT = 15;

export const FAVORITE_TYPE_MAP: favoriteTypeMap = {
	PROJECT: 'PROJECT',
	FILTER: 'FILTER',
	DASHBOARD: 'DASHBOARD',
	PLAN: 'PLAN',
	BOARD: 'BOARD',
	QUEUE: 'QUEUE',
	JPD_COLLECTION: 'JPD_COLLECTION',
};

export type StarredItemType =
	| typeof FAVORITE_TYPE_MAP.PROJECT
	| typeof FAVORITE_TYPE_MAP.PLAN
	| typeof FAVORITE_TYPE_MAP.FILTER
	| typeof FAVORITE_TYPE_MAP.DASHBOARD
	| typeof FAVORITE_TYPE_MAP.BOARD
	| typeof FAVORITE_TYPE_MAP.JPD_COLLECTION
	| typeof FAVORITE_TYPE_MAP.QUEUE;

export const FAVORITE_TYPE_FILTER_MAP: favoriteTypeFilterMap = {
	...FAVORITE_TYPE_MAP,
	ALL: 'ALL',
};

export const VALID_FAVORITE_TYPES: JiraFavouriteType[] = Object.values(FAVORITE_TYPE_MAP);

export const FAVORITE_TYPE_FILTER_MESSAGE_WITH_PROJECT_TERM: {
	[FAVORITE_TYPE_FILTER_MAP.PROJECT]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.FILTER]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.DASHBOARD]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.PLAN]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.BOARD]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.QUEUE]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.ALL]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[FAVORITE_TYPE_FILTER_MAP.PROJECT]: projects,
	[FAVORITE_TYPE_FILTER_MAP.FILTER]: filters,
	[FAVORITE_TYPE_FILTER_MAP.DASHBOARD]: dashboards,
	[FAVORITE_TYPE_FILTER_MAP.PLAN]: plans,
	[FAVORITE_TYPE_FILTER_MAP.BOARD]: boards,
	[FAVORITE_TYPE_FILTER_MAP.QUEUE]: queues,
	[FAVORITE_TYPE_FILTER_MAP.ALL]: all,
	[FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION]: roadmaps,
};

export const FAVORITE_TYPE_FILTER_MESSAGE: {
	[FAVORITE_TYPE_FILTER_MAP.PROJECT]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.FILTER]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.DASHBOARD]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.PLAN]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.BOARD]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.QUEUE]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	[FAVORITE_TYPE_FILTER_MAP.ALL]: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[FAVORITE_TYPE_FILTER_MAP.PROJECT]: projectsGalaxia,
	[FAVORITE_TYPE_FILTER_MAP.FILTER]: filters,
	[FAVORITE_TYPE_FILTER_MAP.DASHBOARD]: dashboards,
	[FAVORITE_TYPE_FILTER_MAP.PLAN]: plans,
	[FAVORITE_TYPE_FILTER_MAP.BOARD]: boards,
	[FAVORITE_TYPE_FILTER_MAP.QUEUE]: queues,
	[FAVORITE_TYPE_FILTER_MAP.JPD_COLLECTION]: roadmaps,
	[FAVORITE_TYPE_FILTER_MAP.ALL]: all,
};

export const SEARCH_STARRED_ITEMS = 'searchStarredItems';
// suppressed to allow migration to typescript-eslint v8 - this is a new error reported with v8 and is only used as a type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const VIEW_ALL_STARRED_ITEMS = 'viewAllStarredItems';

export type FixItemTypeId = typeof SEARCH_STARRED_ITEMS | typeof VIEW_ALL_STARRED_ITEMS;

export const FILTER = 'filter';
